import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Send as SendIcon,
    QrCode2 as QrCodeIcon,
    ContentCopy as CopyIcon,
    Refresh as RefreshIcon,
    ArrowBack as ArrowBackIcon,
    NotificationsActive as ReminderIcon,
    AccountBalanceWallet as WalletIcon,
    People as PeopleIcon,
    CheckCircleOutlined as PaidBadgeIcon,
    HighlightOff as UnpaidBadgeIcon,
    Close as CloseIcon,
    Undo as UndoIcon,
    WhatsApp as WhatsAppIcon,
    PhotoCamera as PhotoCameraIcon,
    AttachFile as AttachFileIcon,
    Check as CheckIcon,
    Cancel as CancelIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { toast } from 'react-toastify';
import configData from '../../configure';
import { useAuth } from '../../AuthContext';
import { getOrgUpiId, setOrgUpiId } from '../../config/orgUpiConfig';
import {
    sendWhatsAppFeeReminder,
    sendBulkWhatsAppFeeReminders,
    openDirectWhatsAppWeb,
    getWhatsAppPaymentScreenshots,
    attachWhatsAppScreenshot,
    updateFeePaymentStatus,
    getAuthToken
} from '../../services/WhatsAppService';
import './StudentPayments.css';

const DEFAULT_FEE_AMOUNT = 0;

export default function StudentPayments({ orgId: propOrgId }) {
    const navigate = useNavigate();
    const { user, token, axios: authAxios, orgId: authOrgId } = useAuth();
    const orgId = propOrgId || authOrgId || user?.orgId;
    const DEFAULT_UPI_ID = getOrgUpiId(orgId);

    // --- State ---
    const [students, setStudents] = useState([]);
    const [classesList, setClassesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedClass, setSelectedClass] = useState('ALL');
    const [selectedTerm, setSelectedTerm] = useState('ALL'); // 'ALL' | installmentId / term description
    const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'PAID' | 'UN-PAID'
    const [searchQuery, setSearchQuery] = useState('');

    // Local payment status persistence map (studentId -> 'PAID' | 'UN-PAID')
    const [paymentStatuses, setPaymentStatuses] = useState(() => {
        try {
            const saved = localStorage.getItem(`student_payment_statuses_${orgId}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });

    // Local WhatsApp send tracking map (studentId -> timestamp)
    const [whatsAppSentMap, setWhatsAppSentMap] = useState(() => {
        try {
            const saved = localStorage.getItem(`student_whatsapp_sent_${orgId}`);
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });

    const [sendingWhatsAppId, setSendingWhatsAppId] = useState(null);

    // Modal state for Send Reminder & QR Code
    const [reminderModal, setReminderModal] = useState({
        open: false,
        student: null,
        message: '',
        sending: false
    });

    // WhatsApp Screenshots Review Queue state
    const [screenshotModal, setScreenshotModal] = useState({
        open: false,
        loading: false,
        screenshots: [],
        selectedImage: null,
        selectedStudentForAttach: {},
        manuallyAttachedWamids: {},
        actionLoading: {}
    });

    // Save payment statuses and WhatsApp sent map to localStorage
    useEffect(() => {
        if (orgId && Object.keys(paymentStatuses).length > 0) {
            try {
                localStorage.setItem(`student_payment_statuses_${orgId}`, JSON.stringify(paymentStatuses));
            } catch (e) {
                console.error("Could not save payment statuses to localStorage", e);
            }
        }
    }, [paymentStatuses, orgId]);

    useEffect(() => {
        if (orgId && Object.keys(whatsAppSentMap).length > 0) {
            try {
                localStorage.setItem(`student_whatsapp_sent_${orgId}`, JSON.stringify(whatsAppSentMap));
            } catch (e) {
                console.error("Could not save WhatsApp tracking to localStorage", e);
            }
        }
    }, [whatsAppSentMap, orgId]);

    // State for Class Fee structures map (classId -> totalFee)
    const [classFeeMap, setClassFeeMap] = useState({});
    const [userPaymentsMap, setUserPaymentsMap] = useState({});
    const [orgDisplayName, setOrgDisplayName] = useState('');

    // Fetch Classes, Class Fee Structures, Students, and Student Payments
    const fetchData = async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            // Fetch Organization details to resolve latest dynamic UPI ID & Org Name
            try {
                const orgRes = await authAxios.get(configData.get_identity_identity_org(orgId));
                if (orgRes.data) {
                    const fetchedName = orgRes.data.orgName || orgRes.data.name || orgRes.data.displayName || orgRes.data.organizationName;
                    if (fetchedName && !/^[0-9a-fA-F]{8}-/.test(fetchedName.trim())) {
                        setOrgDisplayName(fetchedName.trim());
                        localStorage.setItem(`org_name_${orgId}`, fetchedName.trim());
                    }
                    const upi = orgRes.data.customFields?.upiId || orgRes.data.customFields?.upi_id || orgRes.data.upiId;
                    if (upi) {
                        setOrgUpiId(orgId, upi);
                    }
                }
            } catch (eOrg) {
                // skip org fetch error
            }

            // 1. Fetch Classes metadata
            const classesRes = await authAxios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
            const rawClasses = classesRes.data || [];
            setClassesList(rawClasses);
            if (rawClasses.length > 0) {
                const firstClassId = rawClasses[0].classId || rawClasses[0].id;
                setSelectedClass(firstClassId);
            }

            // 2. Fetch Class Fee Structures for each class in parallel
            const TARGET_YEAR = '2025-2026';
            const feeMap = {};
            await Promise.all(rawClasses.map(async (cls) => {
                const cId = cls.classId || cls.id;
                if (!cId) return;
                try {
                    const feeRes = await authAxios.get(`${configData.GET_FEE_STRUCTURE}/class/${cId}?academicYear=${TARGET_YEAR}`);
                    const feeList = Array.isArray(feeRes.data) ? feeRes.data : feeRes.data ? [feeRes.data] : [];
                    const feeData = feeList.find(i => i.academicYear === TARGET_YEAR) || feeList.find(i => i.academicYear?.includes('2025')) || feeList[0];
                    if (feeData) {
                        const rawInstallments = feeData.feeInstallments || feeData.installments || [];
                        let totalClassFee = 0;
                        let firstInstallmentId = null;

                        const parsedInstallments = rawInstallments.map((inst, idx) => {
                            const instTotal = inst.feeBreakdown
                                ? Object.values(inst.feeBreakdown).reduce((a, b) => Number(a || 0) + Number(b || 0), 0)
                                : Number(inst.totalFee || inst.amount || inst.totalAmount || inst.installmentAmount || 0);
                            return {
                                id: inst.installmentId || inst.id || `term-${idx + 1}`,
                                installmentId: inst.installmentId || inst.id || `term-${idx + 1}`,
                                description: inst.description || inst.name || `Term ${idx + 1}`,
                                totalFee: instTotal,
                                amount: instTotal,
                                dueDate: inst.dueDate,
                                startDate: inst.startDate,
                                endDate: inst.endDate,
                                feeBreakdown: inst.feeBreakdown || {}
                            };
                        });

                        if (parsedInstallments.length > 0) {
                            totalClassFee = parsedInstallments.reduce((sum, inst) => sum + inst.totalFee, 0);
                            firstInstallmentId = parsedInstallments[0]?.installmentId;
                        } else {
                            totalClassFee = Number(feeData.totalFee || feeData.totalAmount || feeData.amount || 0);
                        }

                        const feeObj = {
                            totalFee: totalClassFee > 0 ? totalClassFee : 0,
                            feeStructureId: feeData.feeStructureId || feeData.id || null,
                            installmentId: firstInstallmentId || null,
                            academicYear: feeData.academicYear || TARGET_YEAR,
                            installments: parsedInstallments
                        };

                        feeMap[cId] = feeObj;
                        if (cls.classId) feeMap[cls.classId] = feeObj;
                        if (cls.id) feeMap[cls.id] = feeObj;
                        if (cls.className) feeMap[cls.className] = feeObj;
                    } else {
                        feeMap[cId] = { totalFee: 0, feeStructureId: null, installmentId: null, academicYear: TARGET_YEAR, installments: [] };
                    }
                } catch {
                    feeMap[cId] = { totalFee: 0, feeStructureId: null, installmentId: null, academicYear: TARGET_YEAR, installments: [] };
                }
            }));
            setClassFeeMap(feeMap);

            // 3. Fetch Students for all classes
            if (rawClasses.length > 0) {
                const studentPromises = rawClasses.map(async (cls) => {
                    try {
                        const res = await authAxios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
                            params: { page: 0, pageSize: 200 }
                        });
                        const items = res.data?.items || res.data?.content || res.data || [];
                        return items.map(s => ({
                            ...s,
                            classDetails: cls
                        }));
                    } catch {
                        return [];
                    }
                });

                const results = await Promise.all(studentPromises);
                const allStudents = results.flat();
                setStudents(allStudents);

                // 4. Fetch Fee Payments for each student in parallel (filtered for 2025-2026)
                const paymentsMap = {};
                await Promise.all(allStudents.map(async (stu) => {
                    const rawUser = stu.user || stu.rawData?.user || stu;
                    const uId = rawUser.userId || stu.userId || stu.id;
                    if (uId) {
                        try {
                            const pRes = await authAxios.get(`${configData.GET_FEE_PAYMENTS_BY_USER}/${uId}`);
                            const rawList = Array.isArray(pRes.data) ? pRes.data : [];
                            // Filter payments strictly for 2025-2026 academic year and successful status
                            const pList = rawList.filter(p => {
                                const isYearMatch = p.academicYear === TARGET_YEAR || (!p.academicYear && TARGET_YEAR === '2025-2026');
                                const isStatusSuccess = p.paymentStatus === 'SUCCESS' || p.status === 'SUCCESS';
                                return isYearMatch && isStatusSuccess;
                            });
                            const totalPaid = pList.reduce((sum, p) => sum + Number(p.amountPaid || p.amount || 0), 0);
                            paymentsMap[uId] = { totalPaid, count: pList.length, payments: pList };
                        } catch {
                            paymentsMap[uId] = { totalPaid: 0, count: 0, payments: [] };
                        }
                    }
                }));
                setUserPaymentsMap(paymentsMap);
            } else {
                setStudents([]);
            }
        } catch (err) {
            console.error("Error fetching student payment data", err);
            toast.error("Failed to load student payments data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [orgId]);

    // Available Terms for currently selected class
    const availableTerms = useMemo(() => {
        if (selectedClass === 'ALL') {
            const termMap = new Map();
            Object.values(classFeeMap).forEach(feeObj => {
                if (feeObj?.installments) {
                    feeObj.installments.forEach(inst => {
                        if (!termMap.has(inst.description)) {
                            termMap.set(inst.description, inst);
                        }
                    });
                }
            });
            return Array.from(termMap.values());
        }
        const classFeeInfo = classFeeMap[selectedClass] || {};
        return classFeeInfo.installments || [];
    }, [selectedClass, classFeeMap]);

    // Process students with fee info & payment status
    const processedStudents = useMemo(() => {
        return students.map((item, index) => {
            const rawUser = item.user || item.rawData?.user || item;
            const rawStudent = item.studentInfo || item.rawData?.studentInfo || {};
            const studentId = rawUser.userId || item.userId || item.id || `stu-${index}`;

            const admissionNo = rawStudent.admissionId || rawUser.userId || item.admissionNo || `ADM-${1000 + index}`;
            const firstName = rawUser.firstName || item.firstName || 'Student';
            const lastName = rawUser.lastName || item.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            const className = item.classDetails?.className || rawStudent.className || item.className || 'Class 5';
            const sectionName = item.classDetails?.sectionName || rawStudent.sectionName || item.sectionName || 'A';
            const fullClassName = `${className} - ${sectionName}`;
            const phone = rawUser.phoneNumber || rawUser.mobileNo || item.phone || 'N/A';
            const email = rawUser.email || item.email || '';

            // Class Fee amount & structure info from backend
            const classId = item.classDetails?.classId || rawStudent.classId || item.classId || item.raw?.classId;
            const classFeeInfo = classFeeMap[classId] || classFeeMap[item.classDetails?.id] || classFeeMap[item.className] || {};
            const installments = classFeeInfo.installments || [];

            // Determine fee and installment based on selectedTerm
            let feeAmount = 0;
            let installmentId = null;
            let termName = 'Annual Fee';
            let dueDate = null;

            if (selectedTerm === 'ALL') {
                feeAmount = (typeof classFeeInfo.totalFee === 'number' && classFeeInfo.totalFee > 0)
                    ? classFeeInfo.totalFee
                    : (typeof item.totalFee === 'number' && item.totalFee > 0 ? item.totalFee : (typeof item.feeAmount === 'number' && item.feeAmount > 0 ? item.feeAmount : 0));
                installmentId = classFeeInfo.installmentId || (installments[0]?.installmentId) || null;
                termName = 'All Terms (Annual)';
            } else {
                const matchedInst = installments.find(inst => inst.id === selectedTerm || inst.installmentId === selectedTerm || inst.description === selectedTerm);
                if (matchedInst) {
                    feeAmount = matchedInst.totalFee || matchedInst.amount || 0;
                    installmentId = matchedInst.installmentId || matchedInst.id;
                    termName = matchedInst.description;
                    dueDate = matchedInst.dueDate;
                } else {
                    feeAmount = classFeeInfo.totalFee || 0;
                    installmentId = classFeeInfo.installmentId || null;
                    termName = selectedTerm;
                }
            }

            const hasFeeStructure = feeAmount > 0 || Boolean(classFeeInfo.feeStructureId);
            const feeStructureId = classFeeInfo.feeStructureId || item.feeStructureId || null;
            const academicYear = classFeeInfo.academicYear || item.academicYear || '2025-2026';

            // Actual payments from backend
            const studentPaymentData = userPaymentsMap[studentId] || { totalPaid: 0, payments: [] };
            
            // Term-specific payment check
            let isBackendPaid = false;
            let termPaidAmount = 0;
            if (selectedTerm === 'ALL') {
                termPaidAmount = studentPaymentData.totalPaid;
                isBackendPaid = feeAmount > 0 && termPaidAmount >= feeAmount;
            } else {
                const matchedPayments = (studentPaymentData.payments || []).filter(p => {
                    const matchInstId = installmentId && p.installmentId === installmentId;
                    const matchDesc = p.term === termName || p.installmentName === termName || p.description?.includes(termName);
                    return matchInstId || matchDesc;
                });
                if (matchedPayments.length > 0) {
                    termPaidAmount = matchedPayments.reduce((s, p) => s + Number(p.amountPaid || p.amount || 0), 0);
                    isBackendPaid = feeAmount > 0 && (termPaidAmount >= feeAmount || matchedPayments.some(p => p.paymentStatus === 'SUCCESS' || p.status === 'SUCCESS'));
                } else {
                    termPaidAmount = studentPaymentData.totalPaid;
                    isBackendPaid = feeAmount > 0 && termPaidAmount >= feeAmount;
                }
            }

            // Status key with term support for granular local persistence
            const termStatusKey = selectedTerm === 'ALL' ? studentId : `${studentId}_${selectedTerm}`;
            let currentStatus = 'UN-PAID';
            if (!hasFeeStructure && feeAmount === 0 && studentPaymentData.totalPaid === 0) {
                currentStatus = 'NO_STRUCTURE';
            } else if (paymentStatuses[termStatusKey]) {
                currentStatus = paymentStatuses[termStatusKey];
            } else if (paymentStatuses[studentId] && selectedTerm === 'ALL') {
                currentStatus = paymentStatuses[studentId];
            } else if (item.paymentStatus === 'PAID' || isBackendPaid) {
                currentStatus = 'PAID';
            }

            return {
                studentId,
                admissionNo,
                fullName,
                className: fullClassName,
                classId,
                phone,
                email,
                feeAmount,
                hasFeeStructure,
                feeStructureId,
                installmentId,
                academicYear,
                termName,
                dueDate,
                totalPaid: termPaidAmount,
                status: currentStatus, // 'PAID', 'UN-PAID', or 'NO_STRUCTURE'
                termStatusKey,
                raw: item
            };
        });
    }, [students, paymentStatuses, classFeeMap, userPaymentsMap, selectedTerm]);

    // Toggle Payment Status between PAID and UN-PAID
    const handleTogglePaymentStatus = (student) => {
        const newStatus = student.status === 'PAID' ? 'UN-PAID' : 'PAID';
        const key = student.termStatusKey || student.studentId;
        setPaymentStatuses(prev => ({
            ...prev,
            [key]: newStatus
        }));

        if (newStatus === 'PAID') {
            toast.success(`Marked ${student.termName} payment for ${student.fullName} as PAID!`);
        } else {
            toast.info(`Marked ${student.termName} payment for ${student.fullName} as UN-PAID.`);
        }
    };

    // State for Remind All Unpaid modal
    const [remindAllModal, setRemindAllModal] = useState({
        open: false,
        channel: 'WHATSAPP', // Default: 1-click Automatic WhatsApp API
        sending: false,
        progress: { current: 0, total: 0 }
    });

    // Unpaid students filtered by currently selected class
    const unpaidStudentsForClass = useMemo(() => {
        return processedStudents.filter(s => s.status === 'UN-PAID' && (selectedClass === 'ALL' || s.classId === selectedClass));
    }, [processedStudents, selectedClass]);

    // Open Remind All Confirmation Modal for selected class
    const handleRemindAllUnpaid = () => {
        if (unpaidStudentsForClass.length === 0) {
            toast.info("No unpaid students found for the selected class.");
            return;
        }

        setRemindAllModal({
            open: true,
            channel: 'WHATSAPP',
            sending: false,
            progress: { current: 0, total: unpaidStudentsForClass.length }
        });
    };

    // Direct WhatsApp send / resend for an individual student
    const handleSendWhatsAppDirect = async (student) => {
        if (!student) return;
        const targetPhone = student.phone || student.mobileNo || student.contactNo;
        if (!targetPhone) {
            toast.warn(`No phone number available for ${student.fullName}`);
            return;
        }

        setSendingWhatsAppId(student.studentId);
        try {
            const activeOrgName = orgDisplayName || localStorage.getItem(`org_name_${orgId}`) || '';
            const res = await sendWhatsAppFeeReminder({
                orgId,
                orgName: activeOrgName,
                phoneNumber: targetPhone,
                parentName: student.parentName || `Parent of ${student.fullName}`,
                studentName: student.fullName,
                studentId: student.studentId || student.id,
                userId: student.userId || student.parentId || student.studentId,
                classId: student.classId || selectedClass,
                className: student.className || (classesList.find(c => c.classId === selectedClass)?.className) || 'Class',
                feeStructureId: student.feeStructureId,
                installmentId: student.installmentId,
                academicYear: student.academicYear || '2025-2026',
                amount: student.feeAmount,
                pendingAmount: student.feeAmount,
                upiId: DEFAULT_UPI_ID,
                dueDate: 'Immediate',
                paymentUrl: `https://org1.egurukulapps.com`,
                token
            });

            const timestampStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setWhatsAppSentMap(prev => ({
                ...prev,
                [student.studentId]: timestampStr
            }));

            toast.success(`WhatsApp reminder sent to ${student.fullName} (${targetPhone})!`);
        } catch (err) {
            console.error("WhatsApp API dispatch failed, opening direct chat fallback:", err);
            const upiToUse = DEFAULT_UPI_ID || getOrgUpiId(orgId) || 'bsvt@upi';
            const messageText = `Dear Parent,\n\nThis is a gentle fee payment reminder for your ward ${student.fullName} (Admission No: ${student.admissionNo || 'N/A'}, Class: ${student.className || 'Class'}).\n\nThe pending tuition fee amount is ₹${Number(student.feeAmount || 0).toLocaleString('en-IN')}.\n\nKindly make the payment using UPI ID: ${upiToUse}.\n\nThank you,\nEGurukul Management`;
            openDirectWhatsAppWeb(targetPhone, messageText);

            const timestampStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setWhatsAppSentMap(prev => ({
                ...prev,
                [student.studentId]: timestampStr
            }));
            toast.info(`Opened WhatsApp Web chat for ${student.fullName} (${targetPhone})`);
        } finally {
            setSendingWhatsAppId(null);
        }
    };

    // Execute Remind All Dispatch for selected class
    const executeRemindAll = async () => {
        const unpaidList = unpaidStudentsForClass;
        if (unpaidList.length === 0) return;

        const { channel } = remindAllModal;
        setRemindAllModal(prev => ({ ...prev, sending: true }));

        let successCount = 0;
        let failCount = 0;

        // If channel is WHATSAPP or ALL, prepare bulk WhatsApp payload
        if (channel === 'WHATSAPP' || channel === 'ALL') {
            try {
                const whatsAppRecipients = unpaidList.map(s => ({
                    phoneNumber: s.phone || s.mobileNo || s.contactNo,
                    parentName: s.parentName || `Parent of ${s.fullName}`,
                    studentName: s.fullName,
                    pendingAmount: s.feeAmount,
                    dueDate: 'Immediate',
                    paymentUrl: `https://org1.egurukulapps.com`
                })).filter(r => r.phoneNumber);

                if (whatsAppRecipients.length > 0) {
                    await sendBulkWhatsAppFeeReminders({
                        orgId,
                        recipients: whatsAppRecipients,
                        token
                    });

                    const timestampStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const newSentMap = { ...whatsAppSentMap };
                    unpaidList.forEach(s => {
                        newSentMap[s.studentId] = timestampStr;
                    });
                    setWhatsAppSentMap(newSentMap);
                }
            } catch (wErr) {
                console.error("Bulk WhatsApp dispatch error:", wErr);
            }
        }

        // If channel is INBOX or ALL, dispatch via ERP Inbox
        if (channel === 'INBOX' || channel === 'ALL') {
            for (let i = 0; i < unpaidList.length; i++) {
                const student = unpaidList[i];
                setRemindAllModal(prev => ({
                    ...prev,
                    progress: { current: i + 1, total: unpaidList.length }
                }));

                try {
                    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${student.feeAmount}&tn=Fee_${student.admissionNo}`;
                    const upiDirectLink = `upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${student.feeAmount}&tn=Fee_${student.admissionNo}`;

                    const bodyText = `Dear Parent,\n\nThis is an urgent fee payment reminder for your ward ${student.fullName} (Admission No: ${student.admissionNo}, Class: ${student.className}).\n\nThe pending tuition fee amount is ₹${student.feeAmount.toLocaleString('en-IN')}.\n\nKindly make the payment by scanning the attached QR code or tap the payment link below:\n\n🔗 Pay via UPI: [Tap to Pay via UPI](${upiDirectLink})\n\nThank you,\nEGurukul School Management\n\n📎 Payment QR Code: [Scan to Pay](${qrUrl})`;

                    await authAxios.post(configData.INBOX_SEND, {
                        subject: `Fee Payment Reminder - ${student.fullName}`,
                        body: bodyText,
                        orgId: orgId,
                        targetType: 'INDIVIDUAL',
                        targetId: String(student.studentId),
                        category: 'ALERT'
                    }, {
                        params: { userId: user?.userId }
                    });

                    successCount++;
                } catch (err) {
                    console.error(`Failed to send reminder to ${student.fullName}`, err);
                    failCount++;
                }
            }
        } else {
            successCount = unpaidList.length;
        }

        const channelLabel = channel === 'ALL' ? 'ERP Inbox & WhatsApp' : channel === 'WHATSAPP' ? 'WhatsApp API' : 'ERP Inbox';
        toast.success(`Dispatched fee reminders to unpaid students via ${channelLabel}!`);
        setRemindAllModal({ open: false, channel: 'ALL', sending: false, progress: { current: 0, total: 0 } });
    };

    // Open Individual Reminder Modal
    const handleOpenReminder = (student) => {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${student.feeAmount}&tn=Fee_${student.admissionNo}`;
        const upiDirectLink = `upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${student.feeAmount}&tn=Fee_${student.admissionNo}`;

        const messageText = `Dear Parent,\n\nThis is a gentle fee payment reminder for your ward ${student.fullName} (Admission No: ${student.admissionNo}, Class: ${student.className}).\n\nThe pending tuition fee amount is ₹${student.feeAmount.toLocaleString('en-IN')}.\n\nKindly make the payment by scanning the attached QR code or using UPI ID: ${DEFAULT_UPI_ID}.\n\n🔗 Pay via UPI: [Tap to Pay via UPI](${upiDirectLink})\n\nThank you,\nEGurukul School Management`;

        setReminderModal({
            open: true,
            student,
            message: messageText,
            sending: false
        });
    };

    // Send Reminder Notification via Inbox / Email / WhatsApp
    const handleSendReminderAction = async (channel = 'INBOX') => {
        const { student, message } = reminderModal;
        if (!student) return;

        setReminderModal(prev => ({ ...prev, sending: true }));

        try {
            if (channel === 'INBOX') {
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${student.feeAmount}&tn=Fee_${student.admissionNo}`;
                const bodyWithQr = `${message}\n\n📎 Payment QR Code: [Scan to Pay](${qrUrl})`;

                await authAxios.post(configData.INBOX_SEND, {
                    subject: `Fee Payment Reminder - ${student.fullName}`,
                    body: bodyWithQr,
                    orgId: orgId,
                    targetType: 'INDIVIDUAL',
                    targetId: String(student.studentId),
                    category: 'ALERT'
                }, {
                    params: { userId: user?.userId }
                });

                toast.success(`Fee reminder sent to ${student.fullName} via ERP Inbox!`);
            } else if (channel === 'EMAIL') {
                if (!student.email) {
                    toast.warn("No email address registered for this student.");
                    setReminderModal(prev => ({ ...prev, sending: false }));
                    return;
                }

                await authAxios.post(configData.SEND_EMAIL_NOTIFICATION, {
                    to: student.email,
                    subject: `Fee Payment Reminder - ${student.fullName}`,
                    body: message
                });

                toast.success(`Fee reminder email sent to ${student.email}!`);
            } else if (channel === 'WHATSAPP') {
                const targetPhone = student.phone || student.mobileNo || student.contactNo;
                if (!targetPhone) {
                    toast.warn("No phone number registered for this student.");
                    setReminderModal(prev => ({ ...prev, sending: false }));
                    return;
                }

                try {
                    const activeOrgName = orgDisplayName || localStorage.getItem(`org_name_${orgId}`) || '';
                    await sendWhatsAppFeeReminder({
                        orgId,
                        orgName: activeOrgName,
                        phoneNumber: targetPhone,
                        parentName: student.parentName || `Parent of ${student.fullName}`,
                        studentName: student.fullName,
                        studentId: student.studentId || student.id,
                        userId: student.userId || student.parentId || student.studentId,
                        classId: student.classId || selectedClass,
                        className: student.className || (classesList.find(c => c.classId === selectedClass)?.className) || 'Class',
                        installmentId: student.installmentId || 'INST_1',
                        amount: student.feeAmount,
                        pendingAmount: student.feeAmount,
                        upiId: DEFAULT_UPI_ID,
                        dueDate: 'Immediate',
                        paymentUrl: `https://org1.egurukulapps.com`,
                        token
                    });

                    const timestampStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    setWhatsAppSentMap(prev => ({
                        ...prev,
                        [student.studentId]: timestampStr
                    }));

                    toast.success(`WhatsApp reminder sent to ${student.fullName} (${targetPhone})!`);
                } catch (wErr) {
                    console.error("WhatsApp API dispatch failed, opening direct chat fallback:", wErr);
                    openDirectWhatsAppWeb(targetPhone, message);
                    toast.info(`Opening WhatsApp Web chat for ${student.fullName}`);
                }
            }

            setReminderModal({ open: false, student: null, message: '', sending: false });
        } catch (err) {
            console.error("Failed to send reminder", err);
            toast.error("Failed to send reminder notification.");
            setReminderModal(prev => ({ ...prev, sending: false }));
        }
    };

    // Copy Message Text
    const handleCopyMessage = () => {
        navigator.clipboard.writeText(reminderModal.message);
        toast.info("Reminder message copied to clipboard!");
    };

    // Copy UPI ID
    const handleCopyUpi = () => {
        navigator.clipboard.writeText(DEFAULT_UPI_ID);
        toast.info("UPI ID copied: " + DEFAULT_UPI_ID);
    };

    // --- 2. WhatsApp Payment Screenshots Review Queue Handlers ---
    const handleOpenScreenshotReview = async () => {
        setScreenshotModal(prev => ({ ...prev, open: true, loading: true }));
        try {
            const data = await getWhatsAppPaymentScreenshots(orgId, selectedClass, token);
            // Filter out deleted / orphaned records where feePaymentsId was set but feePayment record was deleted
            const validScreenshots = (data || []).filter(item => {
                const sc = item.screenshot || item;
                const fp = item.feePayment;
                if (sc.feePaymentsId && !fp) return false;
                return true;
            });
            setScreenshotModal(prev => ({
                ...prev,
                open: true,
                loading: false,
                screenshots: validScreenshots
            }));
        } catch (err) {
            console.error("Failed to fetch WhatsApp screenshots:", err);
            toast.error("Failed to load WhatsApp payment screenshots.");
            setScreenshotModal(prev => ({ ...prev, loading: false }));
        }
    };

    // 4. Approve Payment
    const handleApprovePayment = async (feePaymentId, studentId) => {
        if (!feePaymentId) return;
        setScreenshotModal(prev => ({
            ...prev,
            actionLoading: { ...prev.actionLoading, [feePaymentId]: true }
        }));
        try {
            await updateFeePaymentStatus(feePaymentId, {
                paymentStatus: 'SUCCESS',
                remarks: 'Verified & Approved via WhatsApp Review',
                verifiedBy: user?.userId || 'ADMIN'
            }, token);

            // Mark student as PAID locally
            if (studentId) {
                setPaymentStatuses(prev => ({ ...prev, [studentId]: 'PAID' }));
            }

            // Update screenshot row status
            setScreenshotModal(prev => ({
                ...prev,
                screenshots: prev.screenshots.map(s => {
                    if (s.screenshot?.feePaymentsId === feePaymentId || s.feePayment?.feePaymentsId === feePaymentId) {
                        return {
                            ...s,
                            feePayment: { ...s.feePayment, paymentStatus: 'SUCCESS' }
                        };
                    }
                    return s;
                }),
                actionLoading: { ...prev.actionLoading, [feePaymentId]: false }
            }));

            toast.success("Payment verified and approved successfully!");
        } catch (err) {
            console.error("Approve failed:", err);
            toast.error(err.response?.data?.message || "Failed to approve payment");
            setScreenshotModal(prev => ({
                ...prev,
                actionLoading: { ...prev.actionLoading, [feePaymentId]: false }
            }));
        }
    };

    // 4. Reject Payment
    const handleRejectPayment = async (feePaymentId) => {
        if (!feePaymentId) return;
        setScreenshotModal(prev => ({
            ...prev,
            actionLoading: { ...prev.actionLoading, [feePaymentId]: true }
        }));
        try {
            await updateFeePaymentStatus(feePaymentId, {
                paymentStatus: 'FAILED',
                remarks: 'Payment screenshot verification rejected by Admin'
            }, token);

            setScreenshotModal(prev => ({
                ...prev,
                screenshots: prev.screenshots.map(s => {
                    if (s.screenshot?.feePaymentsId === feePaymentId || s.feePayment?.feePaymentsId === feePaymentId) {
                        return {
                            ...s,
                            feePayment: { ...s.feePayment, paymentStatus: 'FAILED' }
                        };
                    }
                    return s;
                }),
                actionLoading: { ...prev.actionLoading, [feePaymentId]: false }
            }));

            toast.info("Payment rejected.");
        } catch (err) {
            console.error("Reject failed:", err);
            toast.error(err.response?.data?.message || "Failed to reject payment");
            setScreenshotModal(prev => ({
                ...prev,
                actionLoading: { ...prev.actionLoading, [feePaymentId]: false }
            }));
        }
    };

    // 3. Manual attach unmatched screenshot to a FeePayment or Student ID
    const handleAttachToStudent = async (wamid, selectedVal) => {
        if (!wamid || !selectedVal) {
            toast.warn("Please select a valid student or fee payment to attach.");
            return;
        }
        try {
            let feePaymentIdToAttach = selectedVal;

            // Check if selectedVal is a student ID rather than a feePaymentsId
            const matchedStudent = processedStudents.find(s => s.studentId === selectedVal || s.id === selectedVal);
            if (matchedStudent) {
                // Check if a PENDING feePayment exists for this student
                const studentPayments = userPaymentsMap[matchedStudent.studentId] || userPaymentsMap[matchedStudent.id] || [];
                const pendingPayment = Array.isArray(studentPayments) ? studentPayments.find(p => p.paymentStatus === 'PENDING' || p.status === 'PENDING') : null;

                if (pendingPayment && (pendingPayment.feePaymentsId || pendingPayment.id)) {
                    feePaymentIdToAttach = pendingPayment.feePaymentsId || pendingPayment.id;
                } else {
                    // Create a PENDING FeePayment record silently WITHOUT sending a WhatsApp message to parent
                    const createPayload = {
                        orgId,
                        userId: matchedStudent.studentId || matchedStudent.id,
                        studentId: matchedStudent.studentId || matchedStudent.id,
                        classId: matchedStudent.classId,
                        installmentId: matchedStudent.installmentId || 'ANNUAL_TOTAL',
                        amountPaid: matchedStudent.feeAmount || 0,
                        paymentMode: 'UPI_TRANSFER',
                        recipientPhone: matchedStudent.phone,
                        academicYear: '2025-2026',
                        remarks: 'Created via WhatsApp screenshot manual attachment'
                    };
                    const authHeader = getAuthToken(token);
                    const res = await axios.post(configData.INFRASTRUCTURE_FEEPAYMENT, createPayload, {
                        headers: { Authorization: authHeader, 'Content-Type': 'application/json' }
                    });
                    feePaymentIdToAttach = res?.data?.feePaymentsId || res?.data?.id;
                }
            }

            if (!feePaymentIdToAttach) {
                toast.error("Could not resolve a valid Fee Payment record for this student.");
                return;
            }

            await attachWhatsAppScreenshot({ wamid, feePaymentId: feePaymentIdToAttach }, token);
            toast.success("Screenshot successfully matched and attached to student!");

            // Update local modal state immediately so Approve/Reject buttons appear and badge reflects Manually Attached
            setScreenshotModal(prev => ({
                ...prev,
                manuallyAttachedWamids: { ...prev.manuallyAttachedWamids, [wamid]: true },
                screenshots: (prev.screenshots || []).map(item => {
                    const sc = item.screenshot || item;
                    if (sc.wamid === wamid) {
                        return {
                            ...item,
                            screenshot: { ...sc, feePaymentsId: feePaymentIdToAttach, isManuallyAttached: true },
                            feePayment: item.feePayment || {
                                feePaymentsId: feePaymentIdToAttach,
                                studentId: matchedStudent?.studentId || matchedStudent?.id,
                                studentName: matchedStudent?.fullName,
                                className: matchedStudent?.className,
                                paymentStatus: 'PENDING_VERIFICATION'
                            }
                        };
                    }
                    return item;
                })
            }));

            handleOpenScreenshotReview();
            fetchData();
        } catch (err) {
            console.error("Attach failed:", err);
            toast.error(err.response?.data?.message || "Failed to attach screenshot.");
        }
    };

    // Filtering logic
    const filteredStudents = useMemo(() => {
        return processedStudents.filter(stu => {
            // Class filter
            if (selectedClass !== 'ALL' && stu.classId !== selectedClass) {
                return false;
            }
            // Status filter
            if (statusFilter !== 'ALL' && stu.status !== statusFilter) {
                return false;
            }
            // Search Query
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                const matchName = stu.fullName.toLowerCase().includes(q);
                const matchAdm = stu.admissionNo.toLowerCase().includes(q);
                const matchClass = stu.className.toLowerCase().includes(q);
                const matchPhone = stu.phone.toLowerCase().includes(q);
                return matchName || matchAdm || matchClass || matchPhone;
            }
            return true;
        });
    }, [processedStudents, selectedClass, statusFilter, searchQuery]);

    // Statistics calculations
    const stats = useMemo(() => {
        const total = processedStudents.length;
        const paidCount = processedStudents.filter(s => s.status === 'PAID').length;
        const unpaidCount = processedStudents.filter(s => s.status === 'UN-PAID').length;
        const totalAmountCollected = processedStudents.reduce((sum, s) => sum + (s.totalPaid || 0), 0);
        const totalExpectedAmount = processedStudents.reduce((sum, s) => sum + (s.feeAmount || 0), 0);
        const totalPendingAmount = Math.max(0, totalExpectedAmount - totalAmountCollected);

        return {
            total,
            paidCount,
            unpaidCount,
            totalAmountCollected,
            totalPendingAmount
        };
    }, [processedStudents]);

    return (
        <div className="student-payments-container">
            {/* Top Navigation Banner */}
            {/* Streamlined Toolbar directly above table */}
            <div className="payments-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '300px', flexWrap: 'wrap' }}>
                    {/* 1. Classes dropdown FIRST on the left */}
                    <div className="filter-group" style={{ minWidth: '200px' }}>
                        <select className="filter-control" value={selectedClass} onChange={(e) => {
                            setSelectedClass(e.target.value);
                            setSelectedTerm('ALL');
                        }}>
                            {classesList.map(cls => (
                                <option key={cls.classId || cls.id} value={cls.classId || cls.id}>
                                    {cls.className || cls.name} - {cls.sectionName || cls.section || 'A'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 2. Term Dropdown NEXT to Class dropdown */}
                    <div className="filter-group" style={{ minWidth: '180px' }}>
                        <select 
                            className="filter-control" 
                            value={selectedTerm} 
                            onChange={(e) => setSelectedTerm(e.target.value)}
                            style={{ 
                                fontWeight: 600, 
                                color: '#0B4A8B', 
                                borderColor: '#cbd5e1',
                                background: '#f8fafc' 
                            }}
                        >
                            <option value="ALL">📅 All Terms (Annual)</option>
                            {availableTerms.map((term, idx) => (
                                <option key={term.id || term.installmentId || idx} value={term.id || term.installmentId || term.description}>
                                    {term.description || `Term ${idx + 1}`} {term.totalFee > 0 ? `(₹${term.totalFee.toLocaleString('en-IN')})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="search-box" style={{ flex: 1, minWidth: '200px', maxWidth: '350px' }}>
                        <SearchIcon className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search students"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Review WhatsApp Receipts Button */}
                    <button
                        className="review-receipts-btn"
                        onClick={handleOpenScreenshotReview}
                        disabled={loading}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                            color: '#ffffff',
                            border: 'none',
                            padding: '10px 18px',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <PhotoCameraIcon fontSize="small" style={{ color: '#ffffff' }} />
                        <span>Review Receipts</span>
                    </button>

                    {/* Remind All Unpaid button */}
                    <button
                        className="remind-all-btn"
                        onClick={handleRemindAllUnpaid}
                        disabled={loading || unpaidStudentsForClass.length === 0}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: unpaidStudentsForClass.length === 0 ? '#e2e8f0' : '#f1f5f9',
                            color: unpaidStudentsForClass.length === 0 ? '#94a3b8' : '#0f172a',
                            border: '1px solid #cbd5e1',
                            padding: '10px 18px',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: unpaidStudentsForClass.length === 0 ? 'not-allowed' : 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <ReminderIcon fontSize="small" style={{ color: unpaidStudentsForClass.length === 0 ? '#94a3b8' : '#475569' }} />
                        <span>Remind All ({unpaidStudentsForClass.length})</span>
                    </button>
                </div>
            </div>

            {/* Student Payment Table */}
            <div className="table-card">
                <div className="table-card-header" style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffffff' }}>
                    <div>
                        <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                            Student Fee Payment Status {selectedTerm !== 'ALL' ? `• ${availableTerms.find(t => t.id === selectedTerm || t.installmentId === selectedTerm || t.description === selectedTerm)?.description || selectedTerm}` : ''}
                        </h2>
                    </div>
                </div>
                {loading ? (
                    <div className="loader-container">
                        <CircularProgress color="primary" />
                        <p>Loading student fee payment records...</p>
                    </div>
                ) : filteredStudents.length === 0 ? (
                    <div className="empty-state">
                        <FilterIcon style={{ fontSize: 48, color: '#cbd5e1', marginBottom: 12 }} />
                        <h3>No Student Records Found</h3>
                        <p>No student payment records match your current filters or search query.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="payments-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Admission No</th>
                                    <th>Student Name</th>
                                    <th>Class & Section</th>
                                    <th>Contact</th>
                                    <th>{selectedTerm === 'ALL' ? 'Fee Amount (Annual)' : 'Term Fee'}</th>
                                    <th>Payment Status</th>
                                    <th style={{ textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.map((stu, index) => {
                                    const isPaid = stu.status === 'PAID';
                                    return (
                                        <tr key={stu.studentId} className={isPaid ? 'row-paid' : 'row-unpaid'}>
                                            <td className="col-index">{index + 1}</td>
                                            <td className="col-adm">
                                                <span className="adm-badge">{stu.admissionNo}</span>
                                            </td>
                                            <td className="col-name">
                                                <div className="name-box">
                                                    <span className="stu-name">{stu.fullName}</span>
                                                    {stu.email ? <span className="stu-email">{stu.email}</span> : null}
                                                </div>
                                            </td>
                                            <td className="col-class">
                                                <span className="class-pill">{stu.className}</span>
                                            </td>
                                            <td className="col-phone">{stu.phone}</td>
                                            <td className="col-amount">
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span className="fee-amount" style={stu.hasFeeStructure && stu.feeAmount > 0 ? undefined : { color: '#64748b' }}>
                                                        {stu.hasFeeStructure && stu.feeAmount > 0 ? `₹${stu.feeAmount.toLocaleString('en-IN')}` : 'N/A'}
                                                    </span>
                                                    {selectedTerm !== 'ALL' && (
                                                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginTop: '2px' }}>
                                                            {stu.termName}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="col-status">
                                                {!stu.hasFeeStructure || stu.feeAmount <= 0 ? (
                                                    <span className="status-badge na" style={{ backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                        N/A
                                                    </span>
                                                ) : isPaid ? (
                                                    <span className="status-badge paid">
                                                        <CheckCircleIcon fontSize="inherit" /> Paid
                                                    </span>
                                                ) : (
                                                    <span className="status-badge unpaid">
                                                        <PendingIcon fontSize="inherit" /> Un-paid
                                                    </span>
                                                )}
                                            </td>
                                            <td className="col-actions">
                                                <div className="action-buttons-group">
                                                    {/* Send Reminder Button (Restored Original Icon & Brand Blue Styling) */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <button
                                                            className="send-reminder-btn"
                                                            onClick={() => handleSendWhatsAppDirect(stu)}
                                                            disabled={!stu.hasFeeStructure || stu.feeAmount <= 0 || isPaid || sendingWhatsAppId === stu.studentId}
                                                            style={
                                                                isPaid
                                                                    ? { opacity: 0.6, cursor: 'not-allowed', background: '#f1f5f9', color: '#64748b', border: '1px solid #cbd5e1' }
                                                                    : (!stu.hasFeeStructure || stu.feeAmount <= 0)
                                                                    ? { opacity: 0.5, cursor: 'not-allowed', background: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0' }
                                                                    : sendingWhatsAppId === stu.studentId
                                                                    ? { background: '#014486', color: '#ffffff', opacity: 0.8, cursor: 'wait' }
                                                                    : undefined
                                                            }
                                                            title={
                                                                isPaid
                                                                    ? "Fee is already PAID. Reminder disabled."
                                                                    : (!stu.hasFeeStructure || stu.feeAmount <= 0)
                                                                    ? "No fee structure configured for this class"
                                                                    : `Send fee reminder notification to ${stu.fullName}`
                                                            }
                                                        >
                                                            {sendingWhatsAppId === stu.studentId ? (
                                                                <CircularProgress size={14} style={{ color: '#ffffff' }} />
                                                            ) : (
                                                                <ReminderIcon fontSize="small" />
                                                            )}
                                                            <span>
                                                                {isPaid ? 'Fee Paid' : (!stu.hasFeeStructure || stu.feeAmount <= 0) ? 'No Fee Set' : sendingWhatsAppId === stu.studentId ? 'Sending...' : 'Send Reminder'}
                                                            </span>
                                                        </button>
                                                        {whatsAppSentMap[stu.studentId] && !isPaid && (
                                                            <span
                                                                style={{
                                                                    fontSize: '11px',
                                                                    color: '#059669',
                                                                    fontWeight: 600,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '2px',
                                                                    marginTop: '3px'
                                                                }}
                                                                title={`Last sent at ${whatsAppSentMap[stu.studentId]}`}
                                                            >
                                                                ✓ Sent {whatsAppSentMap[stu.studentId]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Send Fee Reminder & QR Code Dialog */}
            <Dialog
                open={reminderModal.open}
                onClose={() => !reminderModal.sending && setReminderModal({ open: false, student: null, message: '', sending: false })}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: '16px' } }}
            >
                {reminderModal.student && (
                    <>
                        <DialogTitle className="modal-header">
                            <div className="modal-title-left">
                                <QrCodeIcon style={{ color: '#014486', fontSize: 28 }} />
                                <div>
                                    <h3>Send Fee Payment Reminder</h3>
                                    <p>Dispatch reminder notification with QR Code to parent</p>
                                </div>
                            </div>
                            <IconButton
                                size="small"
                                onClick={() => setReminderModal({ open: false, student: null, message: '', sending: false })}
                                disabled={reminderModal.sending}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent className="modal-body-content">
                            <div className="reminder-modal-grid">
                                {/* Left Side: Student Info & Payment QR Code */}
                                <div className="qr-code-card">
                                    <div className="qr-header">
                                        <span className="qr-title">UPI Payment QR Code</span>
                                        <span className="qr-subtitle">Scan using any UPI App (GPay, PhonePe, Paytm)</span>
                                    </div>

                                    <div className="qr-image-container">
                                        <img
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=${DEFAULT_UPI_ID}&pn=EGurukulSchool&am=${reminderModal.student.feeAmount}&tn=Fee_${reminderModal.student.admissionNo}`}
                                            alt="UPI QR Code"
                                            className="qr-code-img"
                                        />
                                    </div>

                                    <div className="upi-id-box">
                                        <span>UPI ID: <strong>{DEFAULT_UPI_ID}</strong></span>
                                        <button className="copy-btn" onClick={handleCopyUpi} title="Copy UPI ID">
                                            <CopyIcon style={{ fontSize: 16 }} />
                                        </button>
                                    </div>

                                    <div className="student-summary-list">
                                        <div className="summary-row">
                                            <span>Student:</span>
                                            <strong>{reminderModal.student.fullName}</strong>
                                        </div>
                                        <div className="summary-row">
                                            <span>Admission No:</span>
                                            <strong>{reminderModal.student.admissionNo}</strong>
                                        </div>
                                        <div className="summary-row">
                                            <span>Class:</span>
                                            <strong>{reminderModal.student.className}</strong>
                                        </div>
                                        <div className="summary-row due">
                                            <span>Pending Due:</span>
                                            <strong className="due-amount">₹{reminderModal.student.feeAmount.toLocaleString('en-IN')}</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side: Reminder Message Editor */}
                                <div className="message-editor-card">
                                    <div className="editor-header-row">
                                        <span className="editor-title">Reminder Message Template</span>
                                        <button className="copy-msg-btn" onClick={handleCopyMessage}>
                                            <CopyIcon style={{ fontSize: 14 }} /> Copy Text
                                        </button>
                                    </div>

                                    <textarea
                                        className="reminder-textarea"
                                        value={reminderModal.message}
                                        onChange={(e) => setReminderModal(prev => ({ ...prev, message: e.target.value }))}
                                        rows={10}
                                    />

                                    <div className="send-channels-bar" style={{ flexWrap: 'wrap', gap: '8px' }}>
                                        <span className="channel-label">Dispatch via:</span>
                                        <div className="channel-buttons" style={{ flexWrap: 'wrap', gap: '8px' }}>
                                            <button
                                                className="channel-btn whatsapp"
                                                onClick={() => handleSendReminderAction('WHATSAPP')}
                                                disabled={reminderModal.sending}
                                                title="Send via Meta WhatsApp Business API"
                                            >
                                                {reminderModal.sending ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon style={{ fontSize: 16 }} />}
                                                <span>WhatsApp API</span>
                                            </button>

                                            <button
                                                className="channel-btn primary"
                                                onClick={() => handleSendReminderAction('INBOX')}
                                                disabled={reminderModal.sending}
                                            >
                                                {reminderModal.sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon style={{ fontSize: 16 }} />}
                                                <span>ERP Inbox</span>
                                            </button>

                                            <button
                                                className="channel-btn secondary"
                                                onClick={() => handleSendReminderAction('EMAIL')}
                                                disabled={reminderModal.sending}
                                            >
                                                <span>Email</span>
                                            </button>

                                            <button
                                                className="channel-btn whatsapp-direct"
                                                onClick={() => openDirectWhatsAppWeb(reminderModal.student.phone || reminderModal.student.mobileNo, reminderModal.message)}
                                                title="Open WhatsApp Web directly in new tab"
                                            >
                                                <span>Direct Web Chat</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogContent>
                    </>
                )}
            </Dialog>

            {/* Remind All Unpaid Students Dialog */}
            <Dialog
                open={remindAllModal.open}
                onClose={() => !remindAllModal.sending && setRemindAllModal({ open: false, channel: 'ALL', sending: false, progress: { current: 0, total: 0 } })}
                maxWidth="sm"
                fullWidth
                PaperProps={{ style: { borderRadius: '16px' } }}
            >
                <DialogTitle className="modal-header">
                    <div className="modal-title-left">
                        <ReminderIcon style={{ color: '#ef4444', fontSize: 28 }} />
                        <div>
                            <h3 style={{ color: '#991b1b' }}>Remind All Unpaid Students</h3>
                            <p>Send fee payment reminders with UPI QR codes & WhatsApp</p>
                        </div>
                    </div>
                    <IconButton
                        size="small"
                        onClick={() => setRemindAllModal({ open: false, channel: 'ALL', sending: false, progress: { current: 0, total: 0 } })}
                        disabled={remindAllModal.sending}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent style={{ padding: '24px' }}>
                    {remindAllModal.sending ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <CircularProgress color="error" size={48} />
                            <h4 style={{ marginTop: '16px', color: '#0f172a' }}>
                                Sending Reminders ({remindAllModal.progress.current} / {remindAllModal.progress.total})...
                            </h4>
                            <p style={{ color: '#64748b', fontSize: '13px' }}>
                                Generating UPI QR codes and dispatching WhatsApp API / ERP Inbox notifications...
                            </p>
                        </div>
                    ) : (
                        <div>
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 6px 0', color: '#991b1b', fontSize: '15px' }}>
                                    Confirm Batch Reminder Dispatch
                                </h4>
                                <p style={{ margin: 0, color: '#7f1d1d', fontSize: '13px', lineHeight: '1.5' }}>
                                    You are about to send personalized fee payment reminders to <strong>{remindAllModal.progress.total} unpaid students</strong>. Each message will contain the student's exact pending fee, generated UPI QR code, and direct payment links.
                                </p>
                            </div>

                            {/* Dispatch Channel Selector */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                                    Select Dispatch Channel:
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                    {[
                                        { id: 'ALL', label: '🚀 Both (Inbox + WhatsApp)', desc: 'Max reach' },
                                        { id: 'WHATSAPP', label: '💬 WhatsApp API', desc: 'Meta Business' },
                                        { id: 'INBOX', label: '✉️ ERP Inbox', desc: 'In-app' }
                                    ].map(ch => (
                                        <button
                                            key={ch.id}
                                            type="button"
                                            onClick={() => setRemindAllModal(prev => ({ ...prev, channel: ch.id }))}
                                            style={{
                                                padding: '10px 8px',
                                                borderRadius: '8px',
                                                border: remindAllModal.channel === ch.id ? '2px solid #25D366' : '1px solid #cbd5e1',
                                                background: remindAllModal.channel === ch.id ? '#f0fdf4' : '#ffffff',
                                                color: remindAllModal.channel === ch.id ? '#166534' : '#475569',
                                                fontWeight: remindAllModal.channel === ch.id ? 700 : 500,
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {ch.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                                <strong>Notification Payload per Student:</strong>
                                <ul style={{ marginTop: '6px', paddingLeft: '20px', color: '#475569' }}>
                                    <li>Student Name, Admission No, and Class Name</li>
                                    <li>Individual Pending Fee Amount</li>
                                    <li>Interactive UPI Payment QR Code & Meta Template</li>
                                    <li>Direct Clickable UPI Link (<code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>upi://pay?pa={DEFAULT_UPI_ID}...</code>)</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </DialogContent>

                <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9' }}>
                    <Button
                        onClick={() => setRemindAllModal({ open: false, channel: 'WHATSAPP', sending: false, progress: { current: 0, total: 0 } })}
                        disabled={remindAllModal.sending}
                        style={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={executeRemindAll}
                        disabled={remindAllModal.sending}
                        style={{
                            background: remindAllModal.channel === 'WHATSAPP' ? '#25D366' : remindAllModal.channel === 'ALL' ? 'linear-gradient(135deg, #25D366, #014486)' : '#014486',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            padding: '8px 22px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                        startIcon={
                            remindAllModal.sending ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : remindAllModal.channel === 'WHATSAPP' ? (
                                <WhatsAppIcon />
                            ) : (
                                <SendIcon />
                            )
                        }
                    >
                        {remindAllModal.sending
                            ? 'Sending...'
                            : remindAllModal.channel === 'WHATSAPP'
                                ? `Send via WhatsApp (${remindAllModal.progress.total})`
                                : remindAllModal.channel === 'ALL'
                                    ? `Send to All Channels (${remindAllModal.progress.total})`
                                    : `Send via ERP Inbox (${remindAllModal.progress.total})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 📸 2. WhatsApp Payment Screenshots Review Queue Dialog */}
            <Dialog
                open={screenshotModal.open}
                onClose={() => setScreenshotModal(prev => ({ ...prev, open: false }))}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: { borderRadius: '16px', overflow: 'hidden', padding: 0 }
                }}
            >
                <DialogTitle style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', color: '#ffffff', padding: '18px 24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <PhotoCameraIcon />
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>WhatsApp Payment Screenshots Queue</h3>
                                <p style={{ margin: '2px 0 0', fontSize: '12px', opacity: 0.9 }}>
                                    Parent payment screenshots captured via WhatsApp Webhook
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IconButton
                                size="small"
                                onClick={handleOpenScreenshotReview}
                                style={{ color: '#ffffff', background: 'rgba(255,255,255,0.2)' }}
                                title="Refresh Queue"
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setScreenshotModal(prev => ({ ...prev, open: false }))}
                                style={{ color: '#ffffff', background: 'rgba(255,255,255,0.2)' }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                </DialogTitle>

                <DialogContent style={{ padding: '20px 24px', background: '#f8fafc', maxHeight: '65vh', overflowY: 'auto' }}>
                    {screenshotModal.loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                            <CircularProgress style={{ color: '#059669', marginBottom: '12px' }} />
                            <span style={{ fontSize: '14px', color: '#64748b' }}>Loading WhatsApp screenshots...</span>
                        </div>
                    ) : (() => {
                        const activeScreenshots = (screenshotModal.screenshots || []).map(item => {
                            const sc = item.screenshot || item;
                            let fp = item.feePayment;
                            if (!fp && sc.feePaymentsId) {
                                const matched = processedStudents.find(s => s.feePaymentsId === sc.feePaymentsId || s.studentId === sc.feePaymentsId) || {};
                                fp = {
                                    feePaymentsId: sc.feePaymentsId,
                                    studentId: matched.studentId || matched.id,
                                    studentName: matched.fullName,
                                    className: matched.className,
                                    paymentStatus: 'PENDING_VERIFICATION'
                                };
                            }
                            return { ...item, screenshot: sc, feePayment: fp };
                        });

                        if (activeScreenshots.length === 0) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px 20px', background: '#ffffff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                    <PhotoCameraIcon style={{ fontSize: '48px', color: '#94a3b8', marginBottom: '10px' }} />
                                    <h4 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '16px' }}>No Pending WhatsApp Receipts</h4>
                                    <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>
                                        When parents reply to fee reminders on WhatsApp with a screenshot, they will appear here automatically for one-click verification.
                                    </p>
                                </div>
                            );
                        }

                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {activeScreenshots.map((item, idx) => {
                                    const sc = item.screenshot || item;
                                    const fp = item.feePayment;
                                    const isMatched = Boolean(sc.feePaymentsId || fp?.feePaymentsId || (fp && (fp.studentId || fp.studentName)));
                                    const matchedStudent = processedStudents.find(s => s.studentId === fp?.studentId || s.id === fp?.studentId);

                                    const cleanScPhone = String(sc.recipientPhone || '').replace(/\D/g, '').slice(-10);
                                    const cleanStuPhone = String(matchedStudent?.phone || '').replace(/\D/g, '').slice(-10);
                                    const isPhoneMismatch = Boolean(cleanScPhone && cleanStuPhone && cleanScPhone !== cleanStuPhone);

                                    const isManuallyAttached = Boolean(
                                        sc.isManuallyAttached ||
                                        screenshotModal.manuallyAttachedWamids?.[sc.wamid] ||
                                        (sc.feePaymentsId && (isPhoneMismatch || fp?.remarks?.includes('attachment') || fp?.remarks?.includes('manual')))
                                    );

                                    const matchBadgeText = !isMatched ? 'Unmatched' : isManuallyAttached ? 'Manually Attached' : null;
                                    const badgeBg = !isMatched ? '#fef3c7' : '#eff6ff';
                                    const badgeColor = !isMatched ? '#d97706' : '#1d4ed8';
                                    const badgeBorder = !isMatched ? '1px solid #fde68a' : '1px solid #bfdbfe';

                                    const isFinalized = fp?.paymentStatus === 'SUCCESS' || fp?.paymentStatus === 'FAILED';
                                    const activeFeePaymentId = fp?.feePaymentsId || sc.feePaymentsId;
                                    const isActionLoading = Boolean(activeFeePaymentId && screenshotModal.actionLoading[activeFeePaymentId]);

                                    return (
                                        <div
                                            key={sc.wamid || idx}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                background: '#ffffff',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                padding: '16px',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                                                gap: '12px'
                                            }}
                                        >
                                            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                                {/* Screenshot Thumbnail with Click-to-Zoom */}
                                                <div
                                                    onClick={() => sc.downloadUrl && setScreenshotModal(prev => ({ ...prev, selectedImage: sc.downloadUrl }))}
                                                    style={{
                                                        width: '110px',
                                                        height: '130px',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        border: '1px solid #cbd5e1',
                                                        background: '#0f172a',
                                                        cursor: sc.downloadUrl ? 'pointer' : 'default',
                                                        position: 'relative',
                                                        flexShrink: 0
                                                    }}
                                                    title="Click to view full screenshot"
                                                >
                                                    {sc.downloadUrl ? (
                                                        <>
                                                            <img
                                                                src={sc.downloadUrl}
                                                                alt="Payment Receipt"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const fallback = e.target.parentElement.querySelector('.img-missing-fallback');
                                                                    if (fallback) fallback.style.display = 'flex';
                                                                }}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                            <div
                                                                className="img-missing-fallback"
                                                                style={{
                                                                    display: 'none',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    color: '#94a3b8',
                                                                    fontSize: '11px',
                                                                    textAlign: 'center',
                                                                    padding: '6px'
                                                                }}
                                                            >
                                                                <PhotoCameraIcon style={{ fontSize: '24px', color: '#64748b', marginBottom: '4px' }} />
                                                                <span>Image Deleted</span>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    background: 'rgba(0,0,0,0.6)',
                                                                    color: '#ffffff',
                                                                    fontSize: '10px',
                                                                    textAlign: 'center',
                                                                    padding: '2px 0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '3px'
                                                                }}
                                                            >
                                                                <VisibilityIcon style={{ fontSize: '12px' }} /> Zoom
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '11px' }}>
                                                            No Preview
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Screenshot & Student Details */}
                                                <div style={{ flex: 1, minWidth: '240px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>
                                                                Phone: {sc.recipientPhone || 'WhatsApp Parent'}
                                                            </span>
                                                            {matchBadgeText && (
                                                                <span
                                                                    style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: 700,
                                                                        padding: '2px 8px',
                                                                        borderRadius: '6px',
                                                                        background: badgeBg,
                                                                        color: badgeColor,
                                                                        border: badgeBorder
                                                                    }}
                                                                >
                                                                    {matchBadgeText}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Status Badge if available */}
                                                        {fp?.paymentStatus && (
                                                            <span
                                                                style={{
                                                                    fontSize: '11px',
                                                                    fontWeight: 700,
                                                                    padding: '3px 10px',
                                                                    borderRadius: '12px',
                                                                    background: fp.paymentStatus === 'SUCCESS' ? '#dcfce7' : fp.paymentStatus === 'FAILED' ? '#fee2e2' : '#fef9c3',
                                                                    color: fp.paymentStatus === 'SUCCESS' ? '#16a34a' : fp.paymentStatus === 'FAILED' ? '#dc2626' : '#ca8a04',
                                                                    border: fp.paymentStatus === 'SUCCESS' ? '1px solid #86efac' : fp.paymentStatus === 'FAILED' ? '1px solid #fca5a5' : '1px solid #fde047'
                                                                }}
                                                            >
                                                                {fp.paymentStatus}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Matched Student Meta (Shows Student Name & Class instead of raw IDs) */}
                                                    {isMatched ? (() => {
                                                        const matchedStudent = processedStudents.find(s => s.studentId === fp?.studentId || s.id === fp?.studentId) || {};
                                                        const studentName = matchedStudent.fullName || fp?.studentName || 'Student';
                                                        const className = matchedStudent.className || fp?.className || '';
                                                        const admissionNo = matchedStudent.admissionNo || matchedStudent.rollNo || '';

                                                        return (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', fontSize: '12px', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>Student Name: </span>
                                                                    <strong style={{ color: '#014486', fontSize: '13px' }}>{studentName}</strong>
                                                                    {className && <span style={{ color: '#64748b', marginLeft: '4px' }}>({className})</span>}
                                                                </div>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>Amount: </span>
                                                                    <strong style={{ color: '#059669', fontSize: '13px' }}>₹{fp?.amountPaid || '0'}</strong>
                                                                </div>
                                                                <div>
                                                                    <span style={{ color: '#64748b' }}>Receipt: </span>
                                                                    <span style={{ color: '#1e293b', fontWeight: 600 }}>{fp?.receiptNumber || 'N/A'}</span>
                                                                </div>
                                                                {admissionNo && (
                                                                    <div>
                                                                        <span style={{ color: '#64748b' }}>Admission No: </span>
                                                                        <span style={{ color: '#1e293b', fontWeight: 600 }}>{admissionNo}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })() : (
                                                        /* 3. Manual Attach Selector for Unmatched Screenshots */
                                                        <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', padding: '10px', borderRadius: '8px', fontSize: '12px' }}>
                                                            <p style={{ margin: '0 0 8px', color: '#92400e', fontWeight: 600 }}>
                                                                ⚠️ No automatic reminder matched. Select a student to attach this receipt:
                                                            </p>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <select
                                                                    style={{ flex: 1, minWidth: '180px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                                                                    value={screenshotModal.selectedStudentForAttach[sc.wamid] || ''}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        setScreenshotModal(prev => ({
                                                                            ...prev,
                                                                            selectedStudentForAttach: { ...prev.selectedStudentForAttach, [sc.wamid]: val }
                                                                        }));
                                                                    }}
                                                                >
                                                                    <option value="">-- Choose Student / Fee Payment --</option>
                                                                    {processedStudents.map(s => (
                                                                        <option key={s.studentId} value={s.studentId}>
                                                                            {s.fullName} ({s.className}) - Due: ₹{s.feeAmount}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => handleAttachToStudent(sc.wamid, screenshotModal.selectedStudentForAttach[sc.wamid])}
                                                                    disabled={!screenshotModal.selectedStudentForAttach[sc.wamid]}
                                                                    style={{ background: '#d97706', color: '#ffffff', textTransform: 'none', fontWeight: 600, fontSize: '11px', borderRadius: '6px' }}
                                                                    startIcon={<AttachFileIcon fontSize="small" />}
                                                                >
                                                                    Attach
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Action Buttons (Approve / Reject) */}
                                            {isMatched && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        disabled={isActionLoading || isFinalized}
                                                        onClick={() => handleRejectPayment(activeFeePaymentId)}
                                                        startIcon={<CancelIcon fontSize="small" />}
                                                        style={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
                                                    >
                                                        Reject
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        disabled={isActionLoading || isFinalized}
                                                        onClick={() => handleApprovePayment(activeFeePaymentId, fp?.studentId)}
                                                        startIcon={isActionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
                                                        style={{
                                                            background: isFinalized ? '#94a3b8' : '#059669',
                                                            color: '#ffffff',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderRadius: '6px',
                                                            boxShadow: 'none'
                                                        }}
                                                    >
                                                        {fp?.paymentStatus === 'SUCCESS' ? 'Already Approved' : 'Approve & Mark Paid'}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}
                </DialogContent>

                <DialogActions style={{ padding: '12px 24px', background: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                    <Button
                        onClick={() => setScreenshotModal(prev => ({ ...prev, open: false }))}
                        style={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 🔍 Lightbox Preview Dialog for Full-Screen Image */}
            <Dialog
                open={Boolean(screenshotModal.selectedImage)}
                onClose={() => setScreenshotModal(prev => ({ ...prev, selectedImage: null }))}
                maxWidth="lg"
                PaperProps={{
                    style: { background: 'transparent', boxShadow: 'none', overflow: 'hidden' }
                }}
            >
                <div style={{ position: 'relative', textAlign: 'center', padding: '20px' }}>
                    <IconButton
                        onClick={() => setScreenshotModal(prev => ({ ...prev, selectedImage: null }))}
                        style={{
                            position: 'absolute',
                            top: '30px',
                            right: '30px',
                            background: 'rgba(0,0,0,0.7)',
                            color: '#ffffff',
                            zIndex: 10
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {screenshotModal.selectedImage && (
                        <img
                            src={screenshotModal.selectedImage}
                            alt="Payment Full Screenshot"
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '85vh',
                                borderRadius: '12px',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </div>
            </Dialog>
        </div>
    );
}
